import { useState, useEffect } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';
import { TermWindow } from './Term';
import SandboxShell from './SandboxShell';
import EvaluationTrace from './EvaluationTrace';
import ResultPanel from './ResultPanel';
import { useRunState, submitLabel } from '@features/ai-prompting/hooks/useRunState';
import { previewCompareOutputs } from '@services/repService';

/**
 * Compare, in two steps (audit P9). The learner used to have to pick A or B, and justify it,
 * BEFORE ever seeing either output: the outputs only came back with the verdict. Now:
 *   1. write prompts A and B, then "run both prompts" -> both outputs appear, labelled A and B
 *      (nothing is scored or saved by this step);
 *   2. only then "which is better?" + the justification + submit. Submitting is unchanged:
 *      the same { promptA, promptB, selection, justification } is scored the same way.
 * Editing either prompt after running makes the outputs stale, so they are hidden and step 2
 * closes until the prompts are run again.
 *
 * Geometry (spec Part 4): two equal columns, stacked under 1200px. Prompts are one row and
 * outputs another, so when stacked the two outputs sit next to each other (A, then B) instead
 * of being split apart by prompt B.
 */
const STACK_QUERY = '(max-width: 1200px)';
const PROSE_MAX_WIDTH = 640;

// Terminal chrome (part 4 item 3) doubles as the A/B label for the prompt windows.
const PromptColumn = ({ repId, side, value, onChange, disabled }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <TermWindow title={`castle-atlas --rep ${repId} -${side.toLowerCase()}`}>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        rows={6}
        style={{
          display: 'block', width: '100%', boxSizing: 'border-box', padding: 12,
          border: 'none', background: disabled ? TT.bgElevated : TT.bgSurface,
          color: TT.text, fontFamily: TT.fontMono, fontSize: 13, lineHeight: 1.55, resize: 'vertical',
        }}
      />
    </TermWindow>
  </div>
);

// What prompt A / prompt B actually produced. pre-wrap keeps the model's line breaks and
// overflowWrap keeps a long unbroken string from forcing sideways scrolling on a narrow screen.
const OutputColumn = ({ side, output, generating }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <TermWindow title={`output ${side} — read only`}>
      <div style={{
        padding: '10px 12px',
        fontFamily: TT.fontSans, fontSize: 12.5, color: output ? TT.text : TT.textDim,
        lineHeight: 1.6, minHeight: 40, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}>
        {output ?? (generating ? 'generating…' : '')}
      </div>
    </TermWindow>
  </div>
);

export const ComparePane = ({ rep, lessonName, principle, topBar, accent, onContinue }) => {
  const [promptA, setPromptA] = useState('');
  const [promptB, setPromptB] = useState('');
  const [selection, setSelection] = useState(null);
  const [justification, setJustification] = useState('');
  // The outputs the learner is choosing between, plus the exact prompts that produced them.
  const [preview, setPreview] = useState(null);                              // { a, b, forA, forB }
  const [previewState, setPreviewState] = useState({ status: 'idle', error: null });   // idle | running | error
  const run = useRunState(rep.rep_id);
  const { phase, result, traceEvents, errorMessage, showCriteria, submit, isLocked, isFailed, isSystemError } = run;

  // A different rep is a clean slate for the preview too.
  useEffect(() => {
    setPreview(null);
    setPreviewState({ status: 'idle', error: null });
  }, [rep.rep_id]);

  const outputsCurrent = !!preview && preview.forA === promptA && preview.forB === promptB;
  const previewing = previewState.status === 'running';
  const canRunPreview = !!(promptA.trim() && promptB.trim()) && !previewing && !isLocked;

  const runPreview = async () => {
    const forA = promptA;
    const forB = promptB;
    setPreviewState({ status: 'running', error: null });
    try {
      const outputs = await previewCompareOutputs({ repId: rep.rep_id, promptA: forA, promptB: forB });
      setPreview({ a: outputs.a, b: outputs.b, forA, forB });
      setPreviewState({ status: 'idle', error: null });
    } catch (err) {
      setPreviewState({
        status: 'error',
        error: err instanceof TypeError
          ? "Can't reach the server. Check your connection and try again."
          : (err.message || 'Something went wrong. Please try again.'),
      });
    }
  };

  const submission = { promptA, promptB, selection, justification };
  const canSubmit = outputsCurrent && !!(selection && justification.trim());
  const label = submitLabel(phase, '$ submit');

  const handleSubmitClick = () => {
    if (phase === 'pass') { onContinue?.(); return; }
    if (phase === 'fail' || phase === 'error') { submit(submission); return; }
    if (canSubmit) submit(submission);
  };

  // The full run still regenerates both outputs (display only; scoring never reads them). The
  // learner already has the ones they chose from, so don't show "generating output A/B" again.
  const evaluationEvents = traceEvents.filter(e => !String(e.stage ?? '').startsWith('generating'));

  const revealedCriteria = showCriteria && result
    ? (result.llm?.feedback?.map(f => ({ name: f.criterion, passed: f.ok }))
      ?? result.deterministic?.checks?.map(c => ({ name: c.name, passed: c.passed }))
      ?? null)
    : null;

  // Part 4 item 5: title is owned solely by the work-area header now.
  const rail = {
    lessonName,
    principle,
    goal: rep.config?.goal,
    constraints: rep.config?.constraints ?? [],
    dimensions: rep.evaluatedOnDimensions ?? [],
    needsHiddenCases: rep.needsHiddenCases,
    revealedCriteria,
  };

  const hint = { fontSize: 12, color: TT.textDim, fontStyle: 'italic', lineHeight: 1.6 };

  return (
    <SandboxShell
      accent={accent}
      topBar={topBar}
      rail={rail}
      formatLabel={rep.format}
      repTitle={rep.title}
      leftColumn={
        <div>
          <div style={{ fontSize: 12, color: TT.textDim, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 4 }}>{'// instruction'}</div>
          <p style={{ fontFamily: TT.fontSans, fontSize: 13.5, color: TT.text, lineHeight: 1.6, margin: '0 0 20px', maxWidth: PROSE_MAX_WIDTH }}>
            {rep.instruction}
          </p>
          <style>{`
            .compare-columns { display: flex; gap: 16px; }
            @media ${STACK_QUERY} { .compare-columns { flex-direction: column; } }
          `}</style>

          {/* Step 1: write both prompts and run them */}
          <div className="compare-columns" style={{ marginBottom: 12 }}>
            <PromptColumn repId={rep.rep_id} side="A" value={promptA} onChange={setPromptA} disabled={isLocked} />
            <PromptColumn repId={rep.rep_id} side="B" value={promptB} onChange={setPromptB} disabled={isLocked} />
          </div>

          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <button
              onClick={runPreview}
              disabled={!canRunPreview}
              className="ps-focusable"
              style={{
                padding: '10px 20px', borderRadius: TT.radius.sm, fontFamily: TT.fontMono,
                fontSize: 13, fontWeight: 700, cursor: canRunPreview ? 'pointer' : 'not-allowed',
                background: 'transparent', color: accent, border: `1px solid ${accent}`,
                opacity: canRunPreview ? 1 : 0.4,
              }}
            >
              {previewing ? 'running both…' : preview ? '$ run both again' : '$ run both prompts'}
            </button>
            {previewState.status === 'error' && (
              <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.cyan }}>⚠ {previewState.error}</span>
            )}
            {!preview && !previewing && previewState.status !== 'error' && (
              <span style={hint}>{'// run both prompts to see their outputs, then choose'}</span>
            )}
            {preview && !outputsCurrent && !previewing && (
              <span style={hint}>{'// you changed a prompt: run both again to see the new outputs'}</span>
            )}
          </div>

          {(previewing || outputsCurrent) && (
            <div className="compare-columns" style={{ marginBottom: 16 }}>
              <OutputColumn side="A" output={outputsCurrent ? preview.a : null} generating={previewing} />
              <OutputColumn side="B" output={outputsCurrent ? preview.b : null} generating={previewing} />
            </div>
          )}

          {/* Step 2: only once the outputs are on screen, choose and justify */}
          {outputsCurrent && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', marginBottom: 6 }}>which is better?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['A', 'B'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => !isLocked && setSelection(opt)}
                      disabled={isLocked}
                      className="ps-focusable"
                      style={{
                        padding: '6px 16px', borderRadius: TT.radius.sm, cursor: isLocked ? 'default' : 'pointer',
                        background: selection === opt ? accent : 'transparent',
                        color: selection === opt ? '#000' : TT.textMuted,
                        border: `1px solid ${selection === opt ? accent : TT.border}`,
                        fontFamily: TT.fontMono, fontSize: 13, fontWeight: 700,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={justification}
                onChange={e => setJustification(e.target.value)}
                disabled={isLocked}
                placeholder="justify your choice, referencing the actual outputs..."
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: TT.radius.sm,
                  background: isLocked ? TT.bgElevated : TT.bgSurface, border: `1px solid ${TT.border}`,
                  color: TT.text, fontFamily: TT.fontMono, fontSize: 13, lineHeight: 1.5, resize: 'vertical',
                }}
              />

              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <button
                  onClick={handleSubmitClick}
                  disabled={phase === 'idle' && !canSubmit}
                  className="ps-focusable"
                  style={{
                    padding: '10px 20px', borderRadius: TT.radius.sm, fontFamily: TT.fontMono,
                    fontSize: 13, fontWeight: 700, cursor: (phase === 'idle' && !canSubmit) ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    color: isFailed ? TT.amber : accent,
                    border: `1px solid ${isFailed ? TT.amber : accent}`,
                    opacity: (phase === 'idle' && !canSubmit) ? 0.4 : 1,
                  }}
                >
                  {label}
                </button>
                {isSystemError && (
                  <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.cyan }}>⚠ {errorMessage}</span>
                )}
              </div>
            </>
          )}

          {(run.isRunning || evaluationEvents.length > 0) && <EvaluationTrace events={evaluationEvents} isCompare />}
          {(phase === 'pass' || phase === 'fail') && <ResultPanel result={result} save={run.save} />}
        </div>
      }
    />
  );
};

export default ComparePane;
